import { useState, useEffect, useRef } from 'react';
import { Loader2, Sticker } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { MediaInfo } from '../types';
import { useMediaUrl } from '../hooks';

interface StickerContentProps {
  media: MediaInfo;
  chatId: number;
  messageId: number;
  className?: string;
}

/**
 * Компонент для отображения стикеров Telegram
 * Поддерживает:
 * - Статические стикеры (WebP)
 * - Анимированные стикеры (TGS/Lottie) - отображаем как статичное превью
 * - Видео стикеры (WebM)
 */
export function StickerContent({ media, chatId, messageId, className }: StickerContentProps) {
  const { url: mediaUrl, isLoading, error } = useMediaUrl(chatId, messageId);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  const isAnimated = media.isAnimatedSticker;
  const isVideo = media.isVideoSticker;

  // Auto-play video stickers on hover
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isVideo) {return;}

    const handleMouseEnter = () => {
      video.play().catch(() => {
        // Ignore autoplay errors
      });
    };

    const handleMouseLeave = () => {
      video.pause();
      video.currentTime = 0;
    };

    video.addEventListener('mouseenter', handleMouseEnter);
    video.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      video.removeEventListener('mouseenter', handleMouseEnter);
      video.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isVideo, mediaUrl]);

  // Size based on sticker dimensions (max 128px)
  const stickerSize = 128;
  const aspectRatio = media.width && media.height ? media.width / media.height : 1;
  const width = aspectRatio >= 1 ? stickerSize : stickerSize * aspectRatio;
  const height = aspectRatio >= 1 ? stickerSize / aspectRatio : stickerSize;

  return (
    <div
      className={cn('relative flex items-center justify-center', className)}
      style={{ width: `${width}px`, height: `${height}px` }}
    >
      {/* Loading state */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Video sticker (WebM) */}
      {isVideo && mediaUrl && !error && (
        <video
          ref={videoRef}
          src={mediaUrl}
          className={cn(
            'max-w-full max-h-full object-contain transition-opacity',
            isVideoLoaded ? 'opacity-100' : 'opacity-0'
          )}
          style={{ width: `${width}px`, height: `${height}px` }}
          loop
          muted
          playsInline
          onLoadedData={() => setIsVideoLoaded(true)}
        />
      )}

      {/* Animated sticker (TGS/Lottie) - показываем как статичное изображение */}
      {/* В будущем можно добавить lottie-web для полной поддержки */}
      {isAnimated && mediaUrl && !error && (
        <div className="relative">
          <img
            src={mediaUrl}
            alt={media.stickerEmoji || 'Стикер'}
            className="max-w-full max-h-full object-contain"
            style={{ width: `${width}px`, height: `${height}px` }}
          />
          {/* Индикатор что это анимированный стикер */}
          <div className="absolute bottom-0 right-0 bg-black/50 text-white text-[8px] px-1 rounded">
            TGS
          </div>
        </div>
      )}

      {/* Static sticker (WebP/PNG) */}
      {!isVideo && !isAnimated && mediaUrl && !error && (
        <img
          src={mediaUrl}
          alt={media.stickerEmoji || 'Стикер'}
          className={cn(
            'max-w-full max-h-full object-contain',
            isLoading && 'hidden'
          )}
          style={{ width: `${width}px`, height: `${height}px` }}
        />
      )}

      {/* Error fallback - show icon */}
      {(error || (!isLoading && !mediaUrl)) && (
        <div
          className="flex items-center justify-center"
          style={{ width: `${width}px`, height: `${height}px` }}
        >
          <Sticker className="h-12 w-12 text-muted-foreground" />
        </div>
      )}
    </div>
  );
}
